import { Fragment, useEffect } from "react";

import CSVSource from "@/components/ai-training/csv-source/csv-source";
import CustomTextSource from "@/components/ai-training/custom-text-source/custom-text-source";
import MultiPageSource from "@/components/ai-training/multi-page-source/multi-page-source";
import PDFSource from "@/components/ai-training/pdf-source/pdf-source";
import SinglePageSource from "@/components/ai-training/single-page-source/single-page-source";
import XMLSource from "@/components/ai-training/xml-source/xml-source";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import useUpdateQueryParams from "@/hooks/use-update-query-params";
import { useSearchParams } from "react-router";
import LinkTable from "../link-table/link-table";
import useSocketMessage from "@/store/socket-message";
import useGlobalPermission from "@/store/user-permissions";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

const SourceSelection = () => {
    const updateQueryParams = useUpdateQueryParams();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (!searchParams.get("source_type")) {
            updateQueryParams({ source_type: "single_page" });
        }
    }, [searchParams, updateQueryParams]);

    const { messages, connectionStatus } = useSocketMessage();
    const { permissionList } = useGlobalPermission();
    const trainingPermission = permissionList.find((item) => item.module.name === "AI_Training");
    return (
        <div className="relative">
            {!trainingPermission?.can_create && (
                <Alert variant="destructive" className="absolute z-10 top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 max-w-2xl">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>You do not have permission to view this list!</AlertDescription>
                </Alert>
            )}
            <Card className={cn("mt-4  border p-4 text-gray-700 shadow-none", !trainingPermission?.can_create && "blur-md")}>
                <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                    <div>
                        <h3 className="text-xl font-bold">Knowledge Base</h3>
                        <p className="text-sm">Manage the sources of knowledge for your AI agent.</p>
                    </div>
                </div>

                <Fragment>
                    <Separator className="my-4" />
                    <h3 className="text-lg font-semibold">Add New Source</h3>
                    <RadioGroup
                        disabled={!trainingPermission?.can_create}
                        value={searchParams.get("source_type") || "single_page"}
                        className="mt-2 flex flex-wrap items-center justify-between"
                        onValueChange={(value) => updateQueryParams({ source_type: value })}
                    >
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="single_page" id="single-page" />
                            <Label htmlFor="single-page">Single Page</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="multi_page" id="multi-page" />
                            <Label htmlFor="multi-page">Multi-Page Website</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="pdf" id="pdf" />
                            <Label htmlFor="pdf">PDF</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="custom_text" id="custom-text" />
                            <Label htmlFor="custom-text">Custom Text</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="csv" id="csv" />
                            <Label htmlFor="csv">List of Links (CSV)</Label>
                        </div>
                    </RadioGroup>
                    {searchParams.get("source_type") === "single_page" && (
                        <div>
                            <Separator className="my-4" />
                            <SinglePageSource />
                        </div>
                    )}
                    {searchParams.get("source_type") === "multi_page" && (
                        <div>
                            <Separator className="my-4" />
                            <MultiPageSource />
                        </div>
                    )}
                    {searchParams.get("source_type") === "pdf" && (
                        <div>
                            <Separator className="my-4" />
                            <PDFSource />
                        </div>
                    )}
                    {searchParams.get("source_type") === "custom_text" && (
                        <div>
                            <Separator className="my-4" />
                            <CustomTextSource />
                        </div>
                    )}
                    {searchParams.get("source_type") === "csv" && (
                        <div>
                            <Separator className="my-4" />
                            <CSVSource />
                        </div>
                    )}
                    {searchParams.get("source_type") === "xml" && (
                        <div>
                            <Separator className="my-4" />
                            <XMLSource />
                        </div>
                    )}
                </Fragment>
            </Card>
            <div className="mb-4">{connectionStatus === "Connected" && messages.length > 0 && <LinkTable />}</div>
        </div>
    );
};

export default SourceSelection;
